import Seo from "@/components/Seo";
import { useEffect, useState } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { api } from "@/lib/api";
import { useSite, siteText } from "@/context/SiteContext";
import { toImageUrl } from "@/lib/driveUrl";
import { toast } from "sonner";
import { FileDown, ImageDown } from "lucide-react";
import AgendaSection from "@/components/AgendaSection";
import { ScaledPoster } from "@/components/poster/PosterCanvas";
import { exportPosterPdf, exportPosterPng } from "@/lib/posterExport";

function safeDate(dateStr) {
  try {
    return format(new Date(dateStr), "d MMMM yyyy", { locale: fr });
  } catch {
    return dateStr;
  }
}

function sortPosters(posters) {
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = posters.filter((p) => !p.date || p.date >= today).sort((a, b) => (a.date || "9999").localeCompare(b.date || "9999"));
  const past = posters.filter((p) => p.date && p.date < today).sort((a, b) => b.date.localeCompare(a.date));
  return [...upcoming, ...past];
}

function posterName(p) {
  return `affiche-${(p.subtitle || p.title || "concert").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "concert"}`;
}

function PosterCard({ poster, index }) {
  const [exporting, setExporting] = useState("");
  const isPast = poster.date && poster.date < new Date().toISOString().slice(0, 10);

  const doExport = async (kind) => {
    setExporting(kind);
    try {
      const name = posterName(poster);
      if (kind === "pdf") await exportPosterPdf(poster, `${name}.pdf`);
      else await exportPosterPng(poster, `${name}.png`);
    } catch {
      toast.error("Le téléchargement a échoué. Réessayez.");
    } finally {
      setExporting("");
    }
  };

  return (
    <article data-testid={`poster-card-${index}`} className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
      <div className="flex flex-col items-center gap-5 p-6 sm:p-8">
        <div className="w-full max-w-sm overflow-hidden rounded-lg border border-stone-200 shadow">
          {poster.image_url ? (
            /* Instantané figé à l'enregistrement : affichage simple et fiable pour les visiteurs */
            <img src={poster.image_url} alt={`Affiche ${poster.subtitle || poster.title || "du concert"}`} className="w-full" loading="lazy" data-testid={`poster-image-${index}`} />
          ) : (
            <ScaledPoster poster={poster} />
          )}
        </div>
        <div className="text-center">
          {poster.date && (
            <span className={`rounded px-2.5 py-1 text-[11px] font-bold uppercase text-white ${isPast ? "bg-stone-400" : "bg-[#B83227]"}`}>
              {isPast ? "Date passée · " : ""}{safeDate(poster.date)}{poster.time ? ` · ${poster.time}` : ""}
            </span>
          )}
          {(poster.subtitle || poster.title) && (
            <h2 className="mt-3 font-display text-2xl font-bold uppercase text-[#1E1B18]">{poster.subtitle || poster.title}</h2>
          )}
        </div>
        <div className="flex flex-wrap justify-center gap-3">
          <button onClick={() => doExport("pdf")} disabled={!!exporting} data-testid={`poster-download-pdf-${index}`} className="inline-flex items-center gap-1.5 rounded bg-[#B83227] px-5 py-2.5 text-xs font-bold uppercase text-white transition-colors duration-300 hover:bg-[#96281E] disabled:opacity-60">
            <FileDown className="h-3.5 w-3.5" /> {exporting === "pdf" ? "Préparation..." : "Télécharger l'affiche (PDF)"}
          </button>
          <button onClick={() => doExport("png")} disabled={!!exporting} data-testid={`poster-download-png-${index}`} className="inline-flex items-center gap-1.5 rounded border border-stone-400 px-5 py-2.5 text-xs font-bold uppercase text-stone-700 transition-colors duration-300 hover:border-stone-600 disabled:opacity-60">
            <ImageDown className="h-3.5 w-3.5" /> {exporting === "png" ? "Préparation..." : "Télécharger en PNG"}
          </button>
        </div>
      </div>
    </article>
  );
}

export default function Actualites() {
  const [news, setNews] = useState([]);
  const [posters, setPosters] = useState([]);
  const { settings } = useSite();

  useEffect(() => {
    api.get("/news").then((r) => setNews(r.data)).catch(() => {});
    api.get("/posters").then((r) => setPosters(sortPosters(r.data))).catch(() => {});
  }, []);

  return (
    <div>
      <Seo title="Actualités" description="Toute l'actualité des Sonneurs de la Côte : concerts, bals, stages et vie du groupe de musique traditionnelle." path="/actualites" />
      <section className="border-b border-stone-200 bg-[#1E1B18] py-16 text-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <span className="text-xs font-bold uppercase tracking-widest text-amber-200">{siteText(settings, "page_actualites_label", "En direct du groupe")}</span>
          <h1 className="mt-2 font-display text-4xl font-bold uppercase tracking-tight sm:text-6xl">{siteText(settings, "page_actualites_title", "Actualités")}</h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-stone-300 sm:text-base">
            {siteText(settings, "page_actualites_intro", "Prochaines dates, comptes-rendus de bals et vie du groupe : tout ce qu'il se passe chez Les Sonneurs de la Côte.")}
          </p>
        </div>
      </section>

      <section className="bg-[#FAF6F0] py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <div className="mb-16">
            <AgendaSection compact />
          </div>
          {posters.length > 0 && (
            <div className="mb-16 space-y-8" data-testid="posters-section">
              <h2 className="font-display text-2xl font-bold uppercase text-[#1E1B18] sm:text-3xl">Nos affiches de concerts</h2>
              {posters.map((p, i) => (
                <PosterCard key={p.id} poster={p} index={i} />
              ))}
            </div>
          )}
          {news.length === 0 && posters.length === 0 ? (
            <p className="rounded-xl border border-dashed border-stone-300 bg-white p-8 text-center text-sm text-stone-500">
              Aucune actualité pour le moment — les prochaines dates seront annoncées ici.
            </p>
          ) : (
            <div className="space-y-8">
              {news.map((n, i) => (
                <article
                  key={n.id}
                  data-testid={`news-card-${i}`}
                  className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm transition-transform duration-300 hover:-translate-y-1"
                >
                  {n.image_url && (
                    <div className="h-56 overflow-hidden" data-image-container>
                      <img src={toImageUrl(n.image_url)} alt={n.title} loading="lazy" onError={(e) => (e.currentTarget.closest("[data-image-container]").style.display = "none")} className="h-full w-full object-cover" />
                    </div>
                  )}
                  <div className="p-6 sm:p-8">
                    <span className="rounded bg-[#B83227] px-2.5 py-1 text-[11px] font-bold uppercase text-white">
                      {safeDate(n.date)}
                    </span>
                    <h2 className="mt-4 font-display text-2xl font-bold uppercase text-[#1E1B18]">{n.title}</h2>
                    <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-stone-700">{n.content}</p>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
